import { useCallback, Suspense, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  type Key,
  Text,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  chatbotAtomFamily_SUSPENDS,
  chatbotsAtom_SUSPENDS,
  closeSecondaryToolbarMenuSetter,
  takeSnapshotSetter,
} from "@zapier/canvas-state";

import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { useEmitResourceConnectEvents } from "../../../hooks/useEmitResourceConnectEvents";
import { ErrorBoundary } from "../../ErrorBoundary";
import { CreateNewChatbotButton } from "../../CreateNewChatbotButton";
import { NewAssetButtonWrapper } from "../NewAssetButtonWrapper";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";
import { SelectedItem } from "../SelectedItem";

import styles from "./FloatingChatbotsTypeahead.module.css";

type Props = {
  selectedChatbotId: string | undefined;
};

function ChatbotsTypeaheadLoader() {
  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search chatbots"
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <TypeaheadItem key="loading" id="loading">
          Loading chatbots...
        </TypeaheadItem>
      </Typeahead>
    </div>
  );
}

function SelectedChatbotSuspended({ selectedChatbotId }: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const selectedChatbot = useAtomValue(
    chatbotAtomFamily_SUSPENDS(selectedChatbotId),
  );

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        chatbotId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={selectedChatbot?.name || "Chatbot not found"}
      icon={<Icon name="navAIChatbot" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function ChatbotsTypeaheadSuspended() {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const chatbots = useAtomValue(chatbotsAtom_SUSPENDS);
  const { emitEventOnChatbotConnect } = useEmitResourceConnectEvents();
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);
  useFocusInput(inputRef);

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          chatbotId: key,
        },
      });

      if (key) {
        emitEventOnChatbotConnect(String(key));
      }

      closeSecondaryMenu();
    },
    [
      takeSnapshot,
      updateNodeAndSave,
      closeSecondaryMenu,
      emitEventOnChatbotConnect,
    ],
  );

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <NewAssetButtonWrapper>
        <CreateNewChatbotButton />
      </NewAssetButtonWrapper>
      <Typeahead
        aria-label="Search chatbots"
        placeholder="Search chatbots"
        ref={inputRef}
        onChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        size="medium"
      >
        {chatbots.map((chatbot) => {
          return (
            <TypeaheadItem
              key={chatbot.id}
              id={chatbot.id}
              textValue={chatbot.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navAIChatbot" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{chatbot.name}</Text>
              </div>
            </TypeaheadItem>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function ChatbotsTypeahead({ selectedChatbotId }: Props) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ChatbotsTypeaheadLoader />}>
        {selectedChatbotId ? (
          <SelectedChatbotSuspended selectedChatbotId={selectedChatbotId} />
        ) : (
          <ChatbotsTypeaheadSuspended />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

export function FloatingChatbotsTypeahead({ selectedChatbotId }: Props) {
  return (
    <NodeToolbarBottom>
      <MenuWrapper>
        <ChatbotsTypeahead selectedChatbotId={selectedChatbotId} />
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
